import React, { useRef, ChangeEvent, useState, useEffect } from 'react';
import classNames from 'classnames';
import PlusIcon from '@jwp/ott-theme/assets/icons/plus.svg?react';
import PlayIcon from '@jwp/ott-theme/assets/icons/play_white.svg?react';
import Icon from '../Icon/Icon';
import styles from './UploadCard.module.scss';
import { formatTimeMs } from '../../utils/formatTime';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';

type Props = {
    file: File | null;
    onFileSelect: (file: File | null) => void;
    onThumbnailExtracted?: (thumbnail: File) => void;
    maxSizeMb?: number;
    duration?: number | null;
    accept?: string;
    disabled?: boolean;
    className?: string;
    onPreview?: () => void;
    label?: string;
    placeholder?: string;
    thumbnailUrl?: string | null;
};

// ✅ Helper function to convert blob to File
const handleBlob = (blob: Blob | null, videoSrc: string, resolve: (value: File) => void, reject: (reason?: Error) => void) => {
    if (blob) {
        const thumbnailFile = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
        resolve(thumbnailFile);
    } else {
        reject(new Error('Canvas to Blob failed'));
    }
    URL.revokeObjectURL(videoSrc);
};

// ✅ Extract first frame from video
const extractVideoThumbnail = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = URL.createObjectURL(file);
        video.muted = true;
        video.playsInline = true;

        video.onloadedmetadata = () => {
            video.currentTime = 0.1;
        };

        video.onseeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(
                (blob) => handleBlob(blob, video.src, resolve, reject),
                'image/jpeg',
                0.8,
            );
        };

        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            reject(new Error('Failed to load video'));
        };
    });
};

const UploadCard = ({
    file,
    onFileSelect,
    onThumbnailExtracted,
    maxSizeMb = 532,
    duration,
    accept = 'video/*',
    disabled,
    className,
    onPreview,
    label,
    placeholder = 'Select Video File',
    thumbnailUrl,
}: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        values: storeValues,
    } = useUploadStore();

    const fileName = disabled ? storeValues?.fileName : file?.name;
    const fileSize = disabled ? storeValues?.fileSize : file?.size;
    const fileSizeNumber = Number(fileSize) || 0;


    useEffect(() => {
        if (file && file.type.startsWith('video/')) {
            // ✅ Only extract if we don't already have a thumbnail
            if (!thumbnailUrl) {  // Don't extract if thumbnail already exists
                extractVideoThumbnail(file)
                    .then((thumbnail) => {
                        onThumbnailExtracted?.(thumbnail);
                    })
                    .catch((error) => {
                        console.error('Failed to extract thumbnail:', error);
                    });
            }
        }
    }, [file, thumbnailUrl]);

    const formatFileSize = (bytes: number): string => {
        const mb = Math.round(bytes / (1024 * 1024));
        return `${mb}Mb`;
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] || null;
        onFileSelect(selectedFile);
    };

    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    return (
        <div className={classNames(styles.uploadCardContainer, className)}>
            {/* ✅ Label/Title */}
            {label && (
                <label className={styles.label}>
                    {label}
                </label>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={disabled}
            />

            {!file && !disabled ? (
                // ✅ Empty state - Single line with icon + text
                <div
                    className={classNames(styles.uploadPromptCard, { [styles.disabled]: disabled })}
                    onClick={() => !disabled && triggerFileSelect()}
                >
                    {/* <Icon icon={PlusIcon} className={styles.plusIcon} /> */}
                    <span className={styles.uploadPromptText}>{placeholder}</span>
                </div>
            ) : (
                // ✅ Selected state
                <div className={styles.card}>
                    <div className={styles.playCircle} onClick={onPreview}>
                        <Icon icon={PlayIcon} className={styles.playIcon} />
                    </div>
                    <div className={classNames(styles.info, { [styles.disabled]: disabled })}>
                        <div className={classNames(styles.fileName, {
                            [styles.editFileName]: disabled,
                        })} title={fileName || ''}>
                            {fileName || ''}
                        </div>

                        <div className={styles.meta}>
                            {duration ? formatTimeMs(duration) : '00:00'} • {formatFileSize(fileSizeNumber)} / {maxSizeMb}Mb
                        </div>
                    </div>

                    {!disabled && <button
                        type="button"
                        className={styles.reUpload}
                        onClick={triggerFileSelect}
                    >
                        Re Upload
                    </button>}
                </div>
            )}
        </div>
    );
};

export default UploadCard;